import React, { useContext, useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { Paintbrush, Send, X, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from './ui/button';

const POLL_INTERVAL_MS = 60 * 1000;

export default function PendingArtworkBanner() {
  const { user, token, API } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [items, setItems] = useState([]);
  const [enabled, setEnabled] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const fetchPending = useCallback(async () => {
    if (!token || !user) return;
    try {
      const res = await axios.get(`${API}/artwork/pending-send`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEnabled(!!res.data?.enabled);
      setItems(res.data?.items || []);
    } catch (e) {
      // silent — don't kick the user out for a banner poll failure
    }
  }, [API, token, user]);

  useEffect(() => {
    fetchPending();
    const t = setInterval(fetchPending, POLL_INTERVAL_MS);
    return () => clearInterval(t);
  }, [fetchPending]);

  // Re-poll when navigating between pages so the banner updates promptly after sending
  useEffect(() => {
    fetchPending();
  }, [location.pathname, fetchPending]);

  if (!enabled || !items.length) return null;
  if (user?.role === 'super_admin') return null;

  const goToOrder = (orderId) => {
    setExpanded(false);
    navigate(`/orders/${orderId}`);
  };

  const totalArtworks = items.reduce((s, i) => s + (i.count || 0), 0);
  const hasCorel = items.some(i => i.has_coreldraw);

  return (
    <div
      className="w-full bg-gradient-to-r from-purple-600 via-fuchsia-600 to-pink-600 text-white shadow-md"
      data-testid="pending-artwork-banner"
    >
      <div className="max-w-7xl mx-auto px-4 py-2.5 flex items-center gap-3">
        <div className="flex items-center gap-2 flex-1">
          <Paintbrush className="w-5 h-5 shrink-0" />
          <div className="text-sm">
            <span className="font-semibold">
              {totalArtworks} artwork {totalArtworks === 1 ? 'proof' : 'proofs'} uploaded
            </span>
            <span className="opacity-90"> across {items.length} {items.length === 1 ? 'order' : 'orders'} — not yet sent to the client</span>
            {hasCorel && (
              <span className="ml-2 hidden sm:inline-flex items-center px-2 py-0.5 rounded-full bg-white/20 text-xs font-medium">
                Includes CorelDRAW uploads
              </span>
            )}
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setExpanded(e => !e)}
          className="text-white hover:bg-white/20"
          data-testid="toggle-pending-artwork-list"
        >
          {expanded ? <ChevronUp className="w-4 h-4 mr-1" /> : <ChevronDown className="w-4 h-4 mr-1" />}
          {expanded ? 'Hide' : 'Review'}
        </Button>
      </div>

      {expanded && (
        <div className="bg-white/10 backdrop-blur-sm border-t border-white/20" data-testid="pending-artwork-list">
          <div className="max-w-7xl mx-auto px-4 py-3 space-y-2 max-h-96 overflow-y-auto">
            {items.map(item => (
              <div
                key={item.order_id}
                className="flex items-center justify-between gap-3 bg-white text-slate-800 rounded-lg px-3 py-2 shadow-sm"
                data-testid={`pending-artwork-${item.order_number}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-purple-700">#{item.order_number}</span>
                    <span className="text-sm truncate">{item.client_name}</span>
                    {item.has_coreldraw && (
                      <span className="text-xs px-1.5 py-0.5 rounded bg-fuchsia-100 text-fuchsia-800 font-medium shrink-0">
                        CorelDRAW
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-slate-500">
                    {item.count} pending {item.count === 1 ? 'proof' : 'proofs'} · {(item.order_type || '').replace(/-/g, ' ')}
                  </div>
                </div>
                <Button
                  size="sm"
                  onClick={() => goToOrder(item.order_id)}
                  className="bg-purple-600 hover:bg-purple-700 text-white shrink-0"
                  data-testid={`send-artwork-${item.order_number}-btn`}
                >
                  <Send className="w-3.5 h-3.5 mr-1" />
                  Send
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
